import React, { useState, useMemo, useContext, useCallback } from 'react';

import { DropdownButton, Card, Toggle, Label, AccordionRow } from '@ergeon/core-components';

import { NoActivitiesEmptyState } from '../../EmptyStates';
import LoadingState from '../../LoadingState';
import { CustomerContext } from '../../../context-providers/CustomerContext';
import useFetch from '../../../hooks/useFetch';
import { useCentrifugoNotifications, useNotificationHandler } from '../../../hooks/useCentrifugoNotifications';
import { getActivities } from '../../../api';
import type { ActivitiesResponse } from '../../../types';
import {
  getActivitiesFilter,
  setActivitiesFilter,
  getActivitiesImportantOnly,
  setActivitiesImportantOnly,
  getAccordionExpanded,
  setAccordionExpanded,
} from '../../../utils/appStorage';

import ActivityCard from './ActivityCard';
import type { Activity } from './types';
import { ActivityFilter, getFilterLabel, getFilterDropdownItems, filterActivities } from './activitiesFilters';
import { convertAPIActivityToActivity } from './activityUtils';

type CustomerActivitiesListProps = Record<string, never>;

const ACCORDION_KEY = 'customerActivities';

const CustomerActivitiesList: React.FC<CustomerActivitiesListProps> = () => {
  const { selectedCustomer } = useContext(CustomerContext);
  const customerId = selectedCustomer?.id;

  const [filter, setFilter] = useState<ActivityFilter>(() => getActivitiesFilter());
  const [showImportant, setShowImportant] = useState<'all' | 'important'>(() =>
    (getActivitiesImportantOnly() ? 'important' : 'all'),
  );
  const [expanded, setExpanded] = useState(() => getAccordionExpanded(ACCORDION_KEY, true));
  const [expandedActivityId, setExpandedActivityId] = useState<number | null>(null);

  const handleFilterChange = useCallback((newFilter: ActivityFilter) => {
    setFilter(newFilter);
    setActivitiesFilter(newFilter);
  }, []);

  const handleShowImportantChange = useCallback((value: 'all' | 'important') => {
    setShowImportant(value);
    setActivitiesImportantOnly(value === 'important');
  }, []);

  const handleExpandChange = useCallback((isExpanded: boolean) => {
    setExpanded(isExpanded);
    setAccordionExpanded(ACCORDION_KEY, isExpanded);
  }, []);

  const handleActivityExpandChange = useCallback((activityId: number, isExpanded: boolean) => {
    setExpandedActivityId(isExpanded ? activityId : null);
  }, []);

  const fetchActivities = useCallback(() => {
    if (!customerId) {
      return Promise.resolve(null);
    }
    return getActivities({ customer: customerId, page_size: 100 });
  }, [customerId]);

  const {
    data: activitiesResponse,
    isLoading,
    refetch,
  } = useFetch<ActivitiesResponse | null>(fetchActivities, [fetchActivities]);

  const handleActivitiesUpdated = useNotificationHandler(customerId, refetch);

  useCentrifugoNotifications({ onActivitiesUpdated: handleActivitiesUpdated });

  const activities: Activity[] = useMemo(() => {
    if (!activitiesResponse?.results) {
      return [];
    }
    return activitiesResponse.results.map(convertAPIActivityToActivity);
  }, [activitiesResponse]);

  const filterDropdownItems = useMemo(
    () => getFilterDropdownItems(filter, handleFilterChange),
    [filter, handleFilterChange],
  );

  const filteredActivities = useMemo(() => {
    let result = filterActivities(activities, filter);

    if (showImportant === 'important') {
      result = result.filter((a) => a.isImportant);
    }

    return result;
  }, [activities, filter, showImportant]);

  const showEmptyState = !isLoading && filteredActivities.length === 0;

  const renderContent = () => {
    if (isLoading) {
      return <LoadingState />;
    }

    if (showEmptyState) {
      return <NoActivitiesEmptyState />;
    }

    return (
      <div className="flex flex-col gap-2">
        {filteredActivities.map((activity) => (
          <ActivityCard
            activity={activity}
            expanded={expandedActivityId === activity.id}
            key={activity.id}
            onExpandChange={(isExpanded) => handleActivityExpandChange(activity.id, isExpanded)} />
        ))}
      </div>
    );
  };

  return (
    <Card className="z-20 flex flex-col gap-3 px-4 py-0 w-full" data-testid="customer-activities-card" flavor="base" zIndex="1">
      <AccordionRow
        expanded={expanded}
        hasBorder={false}
        isFullWidth
        rightSlot={<div className="flex items-center gap-4">
          <DropdownButton
            alignment="left"
            flavor="regular"
            hideChevron={false}
            items={filterDropdownItems}
            label={getFilterLabel(filter)}
            size="small"
            taste="boundless"
            title="Filter activities" />
          <p className="text-gray-500 dark:text-gray-600 paragraph-3">
            {filteredActivities.length} {filteredActivities.length === 1 ? 'Activity' : 'Activities'} Shown
          </p>
          <div className="flex items-center gap-2">
            <Label>Important</Label>
            <Toggle
              items={[
                { label: '', value: 'all' as const },
                { label: '', value: 'important' as const },
              ]}
              onChange={(value) => handleShowImportantChange(value as 'all' | 'important')}
              value={showImportant} />
          </div>
        </div>}
        setExpanded={handleExpandChange}
        title="Activities">
        {renderContent()}
      </AccordionRow>
    </Card>
  );
};

export default CustomerActivitiesList;
